import { useEffect, useReducer } from 'react'
import { Button, Typography, Tooltip, Space, Row, Alert, notification } from 'antd'
import Icon from '@ant-design/icons'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import Web3 from 'web3'
import { useChilizStore, useChilizConfigProvider } from '../stores'
import { collections, INFTCollection } from '../stores/collections'
import { SelectChain } from './SelectChain'
import { chains, IChainConfig } from '../config/chains'
import { Modal } from './Modal'
import { Copy } from './Copy'
import { ExplorerLink } from './ExplorerLink'
import { getWindowSize, IWindowSize } from '../utils/windowSize'
import { truncateHash } from '../utils/truncateHash'

import { ReactComponent as ConnectIconSvg } from '../assets/icons/Connect.svg'
import { ReactComponent as ExternalLink } from '../assets/icons/ExternalLink.svg'
import { ReactComponent as ShieldCheck } from '../assets/icons/ShieldCheck.svg'
import MintingInProgress from '../assets/images/mintingInProgress.png'

const { Title, Text } = Typography

enum EMintModalState {
  CONFIRMATION,
  MINTING,
  MINT_SUCCESSFUL,
  MINT_ERROR,
}

const ModalTitle = ({ state }: {state: EMintModalState}): JSX.Element => {
  switch (state) {
    case EMintModalState.CONFIRMATION:
      return <Text style={{ fontSize: '20px' }}>Minting Confirmation</Text>
    case EMintModalState.MINTING:
      return <Text style={{ fontSize: '20px' }} type='warning'>Minting in process...</Text>
    case EMintModalState.MINT_SUCCESSFUL:
      return <Text style={{ fontSize: '20px', color: '#66BB6A' }}><ShieldCheck width="24px" height="24px" style={{ verticalAlign: 'text-top' }}/> You&lsquo;ve successfully minted your NFT!</Text>
    case EMintModalState.MINT_ERROR:
      return <Text style={{ fontSize: '20px' }} type='danger'>Minting has failed! Please try again.</Text>
    default:
      return <Text style={{ fontSize: '20px' }}>Minting Confirmation</Text>
  }
}

interface IConfirmationModalProps {
  windowSize: IWindowSize
  isWhitelisted: boolean
  collection: INFTCollection
  mintButtonLoading?: boolean
  onMintButtonClick: (...args: any) => any
}
const ConfirmationModal = ({ windowSize, isWhitelisted, collection, mintButtonLoading = false, onMintButtonClick }: IConfirmationModalProps): JSX.Element => {
  if (windowSize.innerWidth < 992) {
    return (
      <Space direction='vertical' align='center' size='middle' style={{ marginTop: '40px' }}>
        <Title level={2} style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, marginBottom: 0 }}>{collection.displayName}</Title>
        <div className="mint-modal-img" style={{ backgroundImage: `url(${collection.coverImage != null ? collection.coverImage : collection.image})` }}></div>
        {// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          !isWhitelisted && <Text type='danger'>{collection.mintModalWhitelistNote}</Text>
        }
        <Button type="primary" loading={mintButtonLoading} onClick={onMintButtonClick} disabled={!isWhitelisted}>Mint a Random NFT</Button>
      </Space>
    )
  } else {
    return (
      <Space align='start' size='middle' style={{ marginTop: '40px' }}>
        <div className="mint-modal-img" style={{ backgroundImage: `url(${collection.coverImage != null ? collection.coverImage : collection.image})` }}></div>
        <Space direction='vertical' style={{ maxWidth: '450px', textAlign: 'left', marginTop: '10px' }} size={30}>
          <Title style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, marginBottom: 0 }}>{collection.displayName}</Title>
          <Text>{collection.description}</Text>
          {// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          !isWhitelisted && <Text type='danger'>{collection.mintModalWhitelistNote}</Text>
          }
          <Button type="primary" loading={mintButtonLoading} onClick={onMintButtonClick} disabled={!isWhitelisted}>Mint a Random NFT</Button>
        </Space>
    </Space>
    )
  }
}

interface IMintingInProgressModalProps {
  collection: INFTCollection
  txHash: string
  faucetUrl: string
  state: EMintModalState
}
const MintingInProgressModal = ({ collection, txHash, faucetUrl, state }: IMintingInProgressModalProps): JSX.Element => {
  return (
    <Space direction='vertical' style={{ marginTop: '40px' }}>
      {state === EMintModalState.MINT_ERROR && (<Row>
        <Text style={{ fontSize: '20px' }} type='danger'>Please make sure you have enough funds in your wallet.<br/>Use the <a href={faucetUrl} target="_blank" rel="noreferrer">faucet</a> to get more CHZ.</Text>
        </Row>
      )}
      <Row justify='center'><Text style={{ fontSize: '18px' }}>Transaction hash: {truncateHash(txHash)}</Text></Row>
      <Row style={{ marginBottom: '40px' }} justify="center">
        <Space size="large">
          <Copy text={txHash}>Copy Hash</Copy>
          <ExplorerLink hash={txHash} type='tx' customTitle={<Text><Icon component={ExternalLink} />View on Explorer</Text>} />
        </Space>
      </Row>
      <img src={MintingInProgress} alt={collection.displayName} style={{ width: '400px', maxWidth: '100%' }}/>
    </Space>
  )
}

interface IMintSuccessfulModalProps {
  windowSize: IWindowSize
  collection: INFTCollection
  tokenId: number
  onSeeInWalletClick: (...args: any) => any
}
const MintSuccessfulModal = ({ windowSize, collection, tokenId, onSeeInWalletClick }: IMintSuccessfulModalProps): JSX.Element => {
  if (windowSize.innerWidth < 992) {
    return (
      <Space direction="vertical" align='center' size='middle' style={{ marginTop: '40px' }}>
        <div className="mint-modal-img" style={{ backgroundImage: `url(${collection.coverImage != null ? collection.coverImage : collection.image})` }}></div>
        <Space direction='vertical' style={{ maxWidth: '450px', textAlign: 'left', marginTop: '10px' }} size={30}>
          <Title style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, marginBottom: 0 }}>
            {collection.displayName}<br/>
            <span style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, fontSize: '30px', lineHeight: '30px', color: '#B6B9BF' }}>#{tokenId}</span>
          </Title>
          <Text>{collection.description}</Text>
          <Button type="primary" size="large" onClick={onSeeInWalletClick}>SEE IN WALLET</Button>
        </Space>
      </Space>
    )
  } else {
    return (
    <Space align='start' size='middle' style={{ marginTop: '40px' }}>
      <div className="mint-modal-img" style={{ backgroundImage: `url(${collection.coverImage != null ? collection.coverImage : collection.image})` }}></div>
      <Space direction='vertical' style={{ maxWidth: '450px', textAlign: 'left', marginTop: '10px' }} size={30}>
        <Title style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, marginBottom: 0 }}>
          {collection.displayName}<br/>
          <span style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, fontSize: '30px', lineHeight: '30px', color: '#B6B9BF' }}>#{tokenId}</span>
        </Title>
        <Text>{collection.description}</Text>
        <Button type="primary" size="large" onClick={onSeeInWalletClick}>SEE IN WALLET</Button>
      </Space>
    </Space>
    )
  }
}

interface IMintButtonProps {
  mintBtnClassName?: string
  isMintBtnBlock?: boolean
  size?: any
}

export const MintButton = observer(({ mintBtnClassName, isMintBtnBlock = false, size = 'middle' }: IMintButtonProps): JSX.Element => {
  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      windowSize: getWindowSize(),
      collectionStep: 0,
      hasExceededMintLimit: false,
      isAccountWhitelisted: true,
      mintModalVisible: false,
      mintModalState: EMintModalState.CONFIRMATION,
      mintTxHash: '',
      mintedTokenId: 0,
      mintButtonLoading: false
    })

  const handleWindowResize = (): void => {
    setState({ windowSize: getWindowSize() })
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const navigate = useNavigate()
  const store = useChilizStore()
  const configProvider = useChilizConfigProvider()
  const featuredCollection = collections[configProvider.getChainConfig().chainId].filter(c => c.featured)[0]
  const onChangeChain = async (chainId): Promise<void> => {
    await store.addNetworkToMetaMask(chains.find(chain => chain.chainId === chainId) as IChainConfig)
  }

  const onMintClick = (): void => setState({ mintModalVisible: true })
  const onMintModalCancelClick = (): void => setState({
    mintModalVisible: false,
    mintModalState: EMintModalState.CONFIRMATION,
    mintTxHash: '',
    mintedTokenId: 0
  })
  const onMintNFTClick = async (): Promise<void> => {
    setState({ mintButtonLoading: true })
    try {
      const { transactionHash, receiptPromise } = await store.mint(featuredCollection.address)
      setState({ mintModalState: EMintModalState.MINTING, mintTxHash: transactionHash })
      const receipt = await receiptPromise
      if (receipt.status) {
        const transferLogs = receipt.logs.filter(l => l.topics[0] === Web3.utils.sha3('Transfer(address,address,uint256)'))
        const tokenId = Web3.utils.hexToNumber(transferLogs[0].topics[3])

        setState({ mintModalState: EMintModalState.MINT_SUCCESSFUL, mintedTokenId: tokenId })
      }
    } catch (error) {
      notification.error({
        message: 'Mint',
        description: `Error: "${(error as Error).message}"`
      })
      setState({ mintModalState: EMintModalState.MINT_ERROR })
    }
    setState({ mintButtonLoading: false })
  }

  useEffect(() => {
    void (async () => {
      try {
        const step = await store.getStep()
        const hasExceeded = await store.hasExceededMintLimit()
        const isWhitelisted = await store.isUserWhitelisted()
        setState({ collectionStep: step, hasExceededMintLimit: hasExceeded, isAccountWhitelisted: isWhitelisted })
      } catch (error) {
        console.log(error)
      }
    })()
  }, [store.isConnected])

  if (state.collectionStep === 2) {
    return <Button type="primary" size={size} style={{ marginRight: '20px' }} className={mintBtnClassName} block={isMintBtnBlock} disabled>SOLD OUT</Button>
  }

  if ([0, 3].includes(state.collectionStep)) {
    return <Button type="primary" size={size} style={{ marginRight: '20px' }} className={mintBtnClassName} block={isMintBtnBlock} disabled>MINTING IS CLOSED</Button>
  }

  if (!store.isConnected) {
    const className = typeof mintBtnClassName !== 'undefined' ? `chain-select connect landing ${mintBtnClassName}` : 'chain-select connect landing'
    // TODO: move this to seperate component?
    return <SelectChain
    defaultValue='CONNECT TO MINT'
    chains={chains}
    className={className}
    style={{ marginRight: '20px', height: '40px', ...(isMintBtnBlock && { width: '100%' }) }}
    suffixIcon={
      <Icon component={ConnectIconSvg}
            style={{
              fill: '#fff',
              color: '#fff',
              fontSize: '24px',
              lineHeight: '24px',
              verticalAlign: 'middle'
            }}/>}
    onChange={async (chain) => await onChangeChain(chain)}/>
  }

  if (state.hasExceededMintLimit === true) {
    return (
      <Tooltip title="You've exceeded the mint limit for this collection" placement='bottom'>
        <Button type="primary" size={size} style={{ marginRight: '20px' }} className={mintBtnClassName} block={isMintBtnBlock} disabled>MINT</Button>
      </Tooltip>
    )
  }

  return (
    <>
      <Button type="primary" size={size} style={{ marginRight: '20px' }} className={mintBtnClassName} block={isMintBtnBlock} onClick={onMintClick}>MINT</Button>
      <Modal
        title=""
        visible={state.mintModalVisible}
        showOkButton={false}
        showCancelButton={false}
        onCancel={onMintModalCancelClick}
      >
        <Space direction='vertical' style={{ marginTop: '40px' }}>
          <ModalTitle state={state.mintModalState} />
          {state.mintModalState === EMintModalState.CONFIRMATION && (
            <ConfirmationModal windowSize={state.windowSize} isWhitelisted={state.isAccountWhitelisted} collection={featuredCollection} mintButtonLoading={state.mintButtonLoading} onMintButtonClick={onMintNFTClick} />
          )}
          {(state.mintModalState === EMintModalState.MINTING || state.mintModalState === EMintModalState.MINT_ERROR) && (
            <MintingInProgressModal collection={featuredCollection} txHash={state.mintTxHash} faucetUrl={configProvider.getChainConfig().faucetUrl} state={state.mintModalState}/>
          )}
          {state.mintModalState === EMintModalState.MINT_SUCCESSFUL && (
            <MintSuccessfulModal windowSize={state.windowSize} collection={featuredCollection} tokenId={state.mintedTokenId} onSeeInWalletClick={() => navigate('/my-wallet')} />
          )}
        </Space>
      </Modal>
    </>
  )
})
